import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';

import { useNavigate, useLocation, useParams } from "react-router-dom";
import ImageSlides from '../components/product/organisms/ImageSlides';
import {
  formatCurrency
} from '../utils'
import Navigation from '../components/commons/organisms/Navigation';
import { LIGHT_PINK_ORANGE, PALE, SOFT_TOMATO } from '../components/colors';
import { Heading1, ProductUnitAmount, ProductUnitType } from '../components/typography';
import QuantitySelector from '../components/commons/molecules/QuantitySelector';
import { useCart } from '../contexts/CartContext';
import APIService from '../utils/api';
import usePageView from '../hooks/use-page-view';
import { MIXPANEL_ACTION, MIXPANEL_PAGE } from '../utils/constants';
import MixpanelService from '../utils/mixpanel';

interface ProductInterface {} 

const Product = ({}: ProductInterface) => {
  const {
    setIsCartOpen,
    addToCart
  } = useCart();

  const navigate = useNavigate();
  const location = useLocation();

  const [product, setProduct] = useState<{
    _id: string;
    description: string;
    units: any[];
    name: string;
    images: string[];
  }>()

  const {
    slug
  } = useParams();

  usePageView(MIXPANEL_PAGE.PRODUCT, { slug });

  const fetchProductBySlug = async () => {
    if (!slug) return;
    try {
      const data = await APIService.getProductBySlug(slug);
      setProduct(data);
    } catch (e) {}

  }

  useEffect(() => {
    fetchProductBySlug();
    return () => {}
  }, [])

  // TODO: FETCH PRODUCT VIA SLUG

  if (!slug || !product) {
    return (
     <>
        <Navigation />
        <Layout>
          <Root>
            <LeftPane>
              <StyledSkeleton style={{ height: 300, background: LIGHT_PINK_ORANGE }}/>
            </LeftPane>
            <Content>
              <TopContent>
                <StyledSkeleton style={{ height: 100 }}/>
              </TopContent>
              <BottomContent>
                <StyledSkeleton style={{ height: 100, background: PALE }}/>
              </BottomContent>
            </Content>
          </Root>
        </Layout>
     </>
    )
  }
    
  return (
    <>
      <Navigation />
      <Layout>
        <Root>
          <LeftPane>
            <ImageSlides 
              images={product.images} 
            />
          </LeftPane>
          <Content>
            <TopContent>
              <Heading1>
                {product.name.toUpperCase()}
              </Heading1>

              <div>
                {product.units.map((unit) => (
                  <Units>
                      <UnitType>
                        <ProductUnitType>{unit.type}</ProductUnitType>
                      </UnitType>
                      <UnitPrice>
                        <ProductUnitAmount> 
                          {formatCurrency(unit.price)}
                        </ProductUnitAmount>
                      </UnitPrice>
                      <ButtonAdd
                        onClick={() => {
                          MixpanelService.track(`${MIXPANEL_PAGE.PRODUCT} - ${MIXPANEL_ACTION.BUTTON_CLICK}`, {
                            item: 'Add To Cart',
                            product: product.name,
                            type: unit.type
                          });
                          addToCart(product, unit.type);
                          setIsCartOpen(true)
                        }}
                      >
                        <p>
                          {`ADD`}
                        </p>
                      </ButtonAdd>
                  </Units>
                ))}
              </div>
            </TopContent>

            <BottomContent>
              <div>
              { product.description.split(`\n`).map((content) => (
                <p>{content.toUpperCase()}</p>   
              ))}
              </div>
            </BottomContent>
          </Content>
        </Root>
      </Layout>
    </>
  )
}

export default Product;

const StyledSkeleton = styled(Skeleton)`
  @media(min-width: 768px) {
    width: 500px;
  }
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;

  @media(min-width: 768px) {
    max-width: 1200px; /* or 1280px */
    margin: 0 auto;
    padding: 1rem 1rem; /* optional side padding */
    flex-direction: row;
  }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;

  @media(min-width: 768px) {
    flex-direction: row;
  }
`;

const LeftPane = styled.div`
  flex: 1;

  @media(min-width: 768px) {
    padding-top: 1rem;
  }
`;

const Content = styled.div`
  flex: 1;
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 1rem;
`;

const BottomContent = styled(TopContent)`
  background-color: ${PALE};
  padding: 1rem 1rem;

  div {
    font-family: 'Poppins-Light';
    font-size: 0.8rem;
    margin: 0 0;

    p {
      margin: 0 auto;
    }
  }
`;

const Units = styled.div`
  display: flex;
  padding: 0.3rem 0rem;
  align-items: center;
`;

const UnitType = styled.div`
  padding: 0.5rem 1rem;
  background-color: #FEF3E5;
  border-radius: 8px;
  margin: 0 0;
`;

const UnitPrice = styled.div`
  flex: 1;
  padding: 0rem 1rem;

  p {
    text-align: left;
    font-weight: 600;
  }
`;

const ButtonAdd = styled.div`
  height: 20px;
  background-color: ${SOFT_TOMATO};
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  
  p {
    margin: 0 0;
    font-family: 'Poppins-SemiBold';
    text-align: center; 
    font-size: 0.8rem;
  }
`;